/**
 * Home Safety Audit — Results Page
 * Route: /home-audit/results
 *
 * Session id resolution order (Pitfall 6):
 *   1. ?sessionId URL query param
 *   2. queryClient cache key homeAuditKeys.completedSession()
 *   3. Error state with link back to /home-audit
 *
 * Print styles inline via <style>{`@media print { ... }`}</style>. The
 * selectors target data-accordion-content within data-state="closed" sections
 * to force-expand them (Pitfall 3). PLAN-08 disclaimer + PLAN-09 print button.
 */

import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Progress } from '@/shared/components/ui/progress';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/utils/cn';
import { homeAuditKeys, useSession } from '../hooks/useHomeAudit';
import { getScoreColor, getScoreLabel } from '../utils/scoreColor';
import { RoomScoreGrid } from './RoomScoreGrid';
import { CostSummaryByTier } from './CostSummaryByTier';
import { ActionPlanAccordion } from './ActionPlanAccordion';
import { QuickWinsCallout } from './QuickWinsCallout';

const DISCLAIMER_BODY =
  'This assessment is a self-guided educational tool intended to help you identify potential home safety improvements. It is not a substitute for a professional home safety inspection or occupational therapy evaluation. Cost estimates are approximate ranges and may vary by location and contractor. Consult a qualified professional before undertaking modifications.';

const PRINT_CSS = `
  @media print {
    nav, header, [data-role="topbar"], .print-hidden { display: none !important; }

    /* Force-expand collapsed accordion sections */
    [data-state="closed"] > [data-accordion-content] {
      display: block !important;
      height: auto !important;
    }

    body { font-size: 9pt; color: #000; }

    /* Compact spacing throughout */
    .space-y-8 > * + * { margin-top: 10pt !important; }
    .space-y-6 > * + * { margin-top: 8pt !important; }
    .space-y-4 > * + * { margin-top: 5pt !important; }
    .space-y-3 > * + * { margin-top: 4pt !important; }

    /* Overall score + room grid side by side */
    [data-testid="room-score-grid"] {
      display: grid !important;
      grid-template-columns: repeat(3, 1fr) !important;
      gap: 3pt !important;
    }
    [data-testid="room-score-grid"] > * { padding: 3pt 5pt !important; }
    [data-testid="room-score-grid"] .text-2xl { font-size: 10pt !important; }
    [data-testid="room-score-grid"] .text-sm,
    [data-testid="room-score-grid"] .text-xs { font-size: 7pt !important; }

    /* Put overall score card left, room grid right in a row */
    .print-score-row {
      display: flex !important;
      flex-direction: row !important;
      gap: 8pt !important;
      align-items: stretch !important;
    }
    .print-score-overall {
      width: 100pt !important;
      flex-shrink: 0 !important;
    }
    .print-score-rooms { flex: 1 !important; }

    /* Compact score hero */
    .text-5xl { font-size: 22pt !important; }

    /* Compact accordion items */
    [data-testid="action-plan-accordion"] [class*="p-4"] { padding: 4pt 6pt !important; }
    [data-testid="action-plan-accordion"] .text-base { font-size: 9pt !important; font-weight: 600; }
    [data-testid="action-plan-accordion"] .text-sm { font-size: 8pt !important; }
    [data-testid="action-plan-accordion"] .text-xs { font-size: 7pt !important; }
    [data-testid="action-plan-accordion"] button { padding: 4pt 6pt !important; }

    /* Cost summary compact */
    [data-testid="cost-summary"] { padding: 6pt !important; }

    /* Disclaimer inline, no page break */
    [data-role="disclaimer"] {
      border: 1pt solid #000;
      padding: 5pt;
      margin-top: 8pt;
      font-size: 7pt;
    }

    * { word-break: break-word; }
  }
`;

export const ResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const urlSessionId = searchParams.get('sessionId');
  const cachedId = queryClient.getQueryData<string>(homeAuditKeys.completedSession());
  const sessionId = urlSessionId ?? cachedId ?? '';

  const { data: session, isLoading, isError } = useSession(sessionId);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl space-y-4">
          <h1 className="text-2xl font-medium">Session not found</h1>
          <p className="text-muted-foreground">
            We couldn&apos;t find your audit session. Start a new audit to get your results.
          </p>
          <Button
            className="bg-[#026857] hover:bg-[#025246] text-white"
            onClick={() => navigate('/home-audit')}
          >
            Start Home Safety Audit
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !session) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl space-y-4">
          <h1 className="text-2xl font-medium">Couldn&apos;t load your audit</h1>
          <p className="text-muted-foreground">
            We couldn&apos;t load your audit. Please refresh the page or start a new audit.
          </p>
          <Button
            className="bg-[#026857] hover:bg-[#025246] text-white"
            onClick={() => navigate('/home-audit')}
          >
            Start Home Safety Audit
          </Button>
        </div>
      </div>
    );
  }

  if (session.status === 'IN_PROGRESS') {
    return <Navigate to="/home-audit" replace />;
  }

  const compositeLabel = getScoreLabel(session.compositeScore);
  const compositeColor = getScoreColor(session.compositeScore);

  return (
    <>
      <style>{PRINT_CSS}</style>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl space-y-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <h1 className="text-3xl font-medium">Your Home Safety Results</h1>
            <Button
              variant="outline"
              className="print-hidden h-11"
              onClick={() => window.print()}
              data-testid="print-button"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Action Plan
            </Button>
          </div>

          <div className="print-score-row space-y-8">
            <Card className={cn('p-6 print-score-overall', compositeColor)}>
              <CardContent className="p-0 space-y-3">
                <p className="text-sm uppercase tracking-wide">Overall Safety Score</p>
                <div className="flex items-baseline gap-3">
                  <p className="text-5xl font-medium">
                    {session.compositeScore?.toFixed(1) ?? '—'}
                  </p>
                  <p className="text-lg">/ 10</p>
                </div>
                <p className="text-base font-medium">{compositeLabel}</p>
                <Progress
                  value={session.compositeScore !== null ? session.compositeScore * 10 : 0}
                  className="h-4"
                />
              </CardContent>
            </Card>

            <section className="space-y-3 print-score-rooms">
              <h2 className="text-xl font-medium print-hidden">Room Scores</h2>
              <RoomScoreGrid session={session} />
            </section>
          </div>

          <QuickWinsCallout actionItems={session.actionItems} />

          <section className="space-y-3">
            <h2 className="text-xl font-medium">Your Action Plan</h2>
            <ActionPlanAccordion actionItems={session.actionItems} />
          </section>

          <section className="space-y-3">
            <CostSummaryByTier actionItems={session.actionItems} />
          </section>

          <Card data-role="disclaimer" className="p-6">
            <CardContent className="p-0 space-y-2">
              <h2 className="text-lg font-medium">Important Disclaimer</h2>
              <p className="text-sm text-muted-foreground">{DISCLAIMER_BODY}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
